import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PasswordField from "../common/PasswordField";
import MozzoFiscalBadge from "../common/MozzoFiscalBadge";
import MerchantApkDownload from "../common/MerchantApkDownload";
import mozzoAppLogoUrl from "../../assets/branding/mozzoapp-kiosk-logo.png";
import {
  applyHeadBrandingAssets,
  applyInstitutionalBrandingAssets,
  WE_PEDIDOS_LOGO_URL,
} from "../../services/local/institutionalBranding";
import { isLocalRuntimeHost } from "../../services/local/runtimeHost";
import {
  getMerchantEntryContinuation,
  MERCHANT_ENTRY_PATH,
  withMerchantEntryContinuation,
} from "../../services/local/merchantEntryNavigation";

const initialForm = {
  email: "",
  password: "",
  confirmPassword: "",
};

function AuthRestaurante({ merchantEntry = false }) {
  const {
    session,
    loadingAuth,
    signIn,
    signUp,
    enterCreatorMode,
    authError,
    isCreatorMode,
    isWaiterDemoMode,
    isSupabaseConfigured,
    missingSupabaseConfig,
  } = useAuth();
  const location = useLocation();
  const merchantContinuation = merchantEntry
    ? MERCHANT_ENTRY_PATH
    : getMerchantEntryContinuation(location);
  const isMerchantEntry = Boolean(merchantContinuation);
  const accessPath = withMerchantEntryContinuation("/acceso", merchantContinuation);

  const [mode, setMode] = useState("login");
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const showLocalPrototypeAccess = isLocalRuntimeHost();

  const missingConfigText = useMemo(
    () => missingSupabaseConfig.join(", "),
    [missingSupabaseConfig]
  );
  const invitationContext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const registerFlag = params.get("register");
    const invitedEmail = String(params.get("email") || "").trim().toLowerCase();
    const invitedRole = String(params.get("role") || "").trim().toLowerCase();

    return {
      forceRegister: registerFlag === "1",
      email: invitedEmail,
      role: invitedRole,
    };
  }, [location.search]);
  const invitationRoleLabel = useMemo(() => {
    switch (invitationContext.role) {
      case "mozo":
        return "mozo";
      case "cocina":
        return "acceso cocina";
      case "admin_limitado":
      default:
        return invitationContext.role ? "admin limitado" : "";
    }
  }, [invitationContext.role]);

  useEffect(() => {
    if (!invitationContext.forceRegister) {
      return;
    }

    setMode("register");
    setLocalError("");
    setSuccessMessage("");
    setForm((prev) => ({
      ...prev,
      email: invitationContext.email || prev.email,
    }));
  }, [invitationContext.email, invitationContext.forceRegister]);

  useEffect(() => {
    if (isMerchantEntry) {
      const previousTitle = document.title;
      document.title = "We!Pedidos Comercios";
      applyHeadBrandingAssets(WE_PEDIDOS_LOGO_URL);
      return () => {
        document.title = previousTitle;
        applyInstitutionalBrandingAssets();
      };
    }
    applyInstitutionalBrandingAssets();
  }, [isMerchantEntry, location.pathname]);

  if (isCreatorMode) {
    return <Navigate to={merchantContinuation || "/app"} replace />;
  }

  if (isWaiterDemoMode) {
    return <Navigate to="/mozo" replace />;
  }

  if (session) {
    return <Navigate to={accessPath} replace />;
  }

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setLocalError("");
    setSuccessMessage("");

    if (!form.email.trim() || !form.password) {
      setLocalError("Completa email y contrasena.");
      return;
    }

    if (mode === "register" && form.password !== form.confirmPassword) {
      setLocalError("Las contrasenas no coinciden.");
      return;
    }

    setSubmitting(true);

    try {
      if (mode === "login") {
        await signIn({
          email: form.email.trim(),
          password: form.password,
        });
      } else {
        const result = await signUp({
          email: form.email.trim(),
          password: form.password,
          redirectTo:
            typeof window !== "undefined"
              ? `${window.location.origin}${accessPath}`
              : undefined,
        });

        setForm(initialForm);

        if (result.requiresEmailConfirmation) {
          setSuccessMessage(
            "Cuenta creada. Revisa tu correo para confirmar el acceso y luego ingresa."
          );
        } else {
          setSuccessMessage(
            "Cuenta creada correctamente. Ya puedes continuar con la configuracion de tu empresa."
          );
        }
      }
    } catch (error) {
      setLocalError(error.message || "No pudimos procesar la solicitud.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ ...styles.page, ...(isMerchantEntry ? styles.merchantPage : {}) }}>
      <div style={styles.panel}>
        <div
          role="img"
          aria-label={isMerchantEntry ? "We!Pedidos Comercios" : "MozzoApp"}
          style={{
            ...styles.brandLogo,
            ...(isMerchantEntry ? styles.merchantLogo : {}),
            backgroundImage: `url("${isMerchantEntry ? WE_PEDIDOS_LOGO_URL : mozzoAppLogoUrl}")`,
          }}
        />
        <h1 style={styles.title}>
          {isMerchantEntry ? "We!Pedidos Comercios" : "Panel para empresas gastronomicas"}
        </h1>
        <p style={styles.subtitle}>
          {isMerchantEntry
            ? "Ingresa con tu cuenta de comercio para administrar delivery, restaurante y sucursales."
            : "Accede con tu cuenta para administrar empresas, sucursales, menu, ingredientes y operacion diaria."}
        </p>
        <MerchantApkDownload />
        <p style={styles.helperText}>
          Si un admin general te invito como admin limitado, entra o crea tu cuenta con ese
          mismo email para activar el acceso.
        </p>
        {invitationContext.forceRegister && invitationContext.email ? (
          <div style={{ ...styles.banner, ...styles.bannerInfo }}>
            Te invitaron como {invitationRoleLabel || "usuario operativo"} con{" "}
            <strong>{invitationContext.email}</strong>. Crea la cuenta con ese mismo correo,
            confirma el email y despues ingresa para entrar directo al acceso que te cargaron.
          </div>
        ) : null}

        {!isSupabaseConfigured && (
          <div style={{ ...styles.banner, ...styles.bannerWarning }}>
            Falta configurar Supabase en el frontend: {missingConfigText}
          </div>
        )}

        {showLocalPrototypeAccess ? (
          <>
            <button
              type="button"
              style={styles.creatorButton}
              onClick={enterCreatorMode}
            >
              Entrar como creador
            </button>

            <button
              type="button"
              style={styles.waiterDemoButton}
              onClick={() => window.location.assign("/mozo-demo")}
            >
              Entrar como mozo demo
            </button>

            <Link to="/colaboradores" style={styles.collaboratorButton}>
              Abrir programa de colaboradores
            </Link>
          </>
        ) : null}

        <div style={styles.switchRow}>
          <button
            type="button"
            style={{
              ...styles.switchButton,
              ...(mode === "login" ? styles.switchButtonActive : {}),
            }}
            onClick={() => {
              setMode("login");
              setLocalError("");
              setSuccessMessage("");
            }}
          >
            Ingresar
          </button>
          <button
            type="button"
            style={{
              ...styles.switchButton,
              ...(mode === "register" ? styles.switchButtonActive : {}),
            }}
            onClick={() => {
              setMode("register");
              setLocalError("");
              setSuccessMessage("");
            }}
          >
            Crear cuenta
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            Email
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={(event) => handleChange("email", event.target.value)}
              style={styles.input}
              placeholder="empresa@correo.com"
              disabled={submitting || loadingAuth || !isSupabaseConfigured}
              autoComplete="email"
            />
          </label>

          <PasswordField
            label="Contrasena"
            name="password"
            value={form.password}
            onChange={(event) => handleChange("password", event.target.value)}
            placeholder="Minimo 6 caracteres"
            disabled={submitting || loadingAuth || !isSupabaseConfigured}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
          />

          {mode === "login" && (
            <div style={styles.secondaryActionRow}>
              <Link
                to={withMerchantEntryContinuation("/recuperar-contrasena", merchantContinuation)}
                style={styles.secondaryLink}
              >
                &iquest;Olvidaste tu contrase&ntilde;a?
              </Link>
            </div>
          )}

          {mode === "register" && (
            <PasswordField
              label="Repetir contrasena"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={(event) =>
                handleChange("confirmPassword", event.target.value)
              }
              placeholder="Repite la contrasena"
              disabled={submitting || loadingAuth || !isSupabaseConfigured}
              autoComplete="new-password"
            />
          )}

          {(localError || authError) && (
            <div style={{ ...styles.banner, ...styles.bannerError }}>
              {localError || authError}
            </div>
          )}

          {successMessage && (
            <div style={{ ...styles.banner, ...styles.bannerSuccess }}>
              {successMessage}
            </div>
          )}

          <button
            type="submit"
            style={styles.submitButton}
            disabled={submitting || loadingAuth || !isSupabaseConfigured}
          >
            {submitting
              ? mode === "login"
                ? "Ingresando..."
                : "Creando cuenta..."
              : mode === "login"
                ? "Entrar al panel"
                : "Crear acceso"}
          </button>
        </form>

        <footer style={styles.legalFooter}>
          <p style={styles.legalIdentity}>
            MozzoApp es el nombre comercial de{" "}
            <strong>PORTILLO DIEGO ALBERTO</strong>.
          </p>
          <Link to="/politica-de-privacidad" style={styles.legalLink}>
            Politica de privacidad
          </Link>
          <MozzoFiscalBadge />
        </footer>
      </div>
    </div>
  );
}

const styles = {
  merchantPage: {
    background: "linear-gradient(145deg, #fff7ed 0%, #ffffff 55%, #ffedd5 100%)",
  },
  merchantLogo: {
    height: "96px",
    backgroundSize: "contain",
    backgroundPosition: "left center",
  },
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    background:
      "linear-gradient(145deg, rgba(241,245,249,1) 0%, rgba(248,250,252,1) 55%, rgba(226,232,240,1) 100%)",
  },
  panel: {
    width: "100%",
    maxWidth: "520px",
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    padding: "32px",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.12)",
    border: "1px solid rgba(148, 163, 184, 0.2)",
  },
  brandLogo: {
    width: "240px",
    maxWidth: "100%",
    height: "56px",
    marginBottom: "18px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center center",
    backgroundSize: "260px auto",
  },
  title: {
    marginTop: 0,
    marginBottom: "8px",
    color: "#0f172a",
  },
  subtitle: {
    marginTop: 0,
    marginBottom: "8px",
    color: "#475569",
    lineHeight: 1.5,
  },
  helperText: {
    marginTop: 0,
    marginBottom: "24px",
    color: "#64748b",
    lineHeight: 1.5,
    fontSize: "14px",
  },
  switchRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "20px",
  },
  creatorButton: {
    width: "100%",
    marginBottom: "16px",
    border: "1px solid #a5b4fc",
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    borderRadius: "12px",
    padding: "12px 14px",
    cursor: "pointer",
    fontWeight: 700,
  },
  waiterDemoButton: {
    width: "100%",
    marginBottom: "16px",
    border: "1px solid #99f6e4",
    backgroundColor: "#ecfeff",
    color: "#155e75",
    borderRadius: "12px",
    padding: "12px 14px",
    cursor: "pointer",
    fontWeight: 700,
  },
  collaboratorButton: {
    width: "100%",
    display: "block",
    boxSizing: "border-box",
    marginBottom: "16px",
    border: "1px solid #fdba74",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    borderRadius: "12px",
    padding: "12px 14px",
    textAlign: "center",
    textDecoration: "none",
    fontWeight: 700,
  },
  switchButton: {
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    borderRadius: "12px",
    padding: "12px 14px",
    cursor: "pointer",
    fontWeight: 600,
  },
  switchButtonActive: {
    backgroundColor: "#0f172a",
    color: "#ffffff",
    borderColor: "#0f172a",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  secondaryActionRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "-2px",
  },
  secondaryLink: {
    color: "#2563eb",
    fontWeight: 600,
    textDecoration: "none",
    fontSize: "14px",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: "#1e293b",
    fontWeight: 600,
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
  },
  submitButton: {
    marginTop: "8px",
    width: "100%",
    border: "none",
    borderRadius: "14px",
    padding: "14px 16px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  legalFooter: {
    marginTop: "24px",
    paddingTop: "18px",
    borderTop: "1px solid #e2e8f0",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.55,
    textAlign: "center",
  },
  legalIdentity: {
    margin: "0 0 6px",
  },
  legalLink: {
    color: "#475569",
    fontWeight: 600,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
  },
  banner: {
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "14px",
    lineHeight: 1.45,
  },
  bannerWarning: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#9a3412",
    marginBottom: "18px",
  },
  bannerError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  bannerSuccess: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  bannerInfo: {
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    marginBottom: "18px",
  },
};

export default AuthRestaurante;
